// Ocorrencias previstas de publicacoes recorrentes num dia do calendario.

import { useMemo } from "react";
import { Link } from "@tanstack/react-router";
import { Repeat } from "lucide-react";
import { useRecurringPosts } from "@/lib/hooks/useRecurringPosts";
import { monthRange, recurringOccurrencesByDate, type RecurringOccurrence } from "@/lib/recurring-calendar";

export type RecurringByDate = ReadonlyMap<string, readonly RecurringOccurrence[]>;

/** Ocorrencias futuras das recorrencias ativas no mes exibido. */
export function useRecurringDays(year: number, month: number): RecurringByDate {
  const { data: posts = [] } = useRecurringPosts();
  return useMemo(() => {
    const { from, until } = monthRange(year, month);
    return recurringOccurrencesByDate(posts, from, until);
  }, [posts, year, month]);
}

/** Marcador no dia do calendario com recorrente prevista. */
export function RecurringMark({ byDate, date }: { byDate: RecurringByDate; date: string }): JSX.Element | null {
  if (!byDate.has(date)) return null;
  return <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full border border-primary" title="Recorrente prevista" />;
}

function formatTime(date: Date): string {
  return new Intl.DateTimeFormat("pt-BR", { timeZone: "America/Sao_Paulo", hour: "2-digit", minute: "2-digit" }).format(date);
}

export function RecurringDayList({ byDate, date }: { byDate: RecurringByDate; date: string | null }): JSX.Element | null {
  const items = date ? (byDate.get(date) ?? []) : [];
  if (items.length === 0) return null;
  return (
    <div className="border-t border-border px-5 py-3">
      <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground">
        <Repeat size={12} /> Recorrentes previstas
      </p>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={`${item.recurringId}-${item.at.toISOString()}`} className="text-xs">
            <span className="mr-2 font-semibold tabular-nums text-foreground">{formatTime(item.at)}</span>
            <span className="text-muted-foreground">{item.bodyText.length > 60 ? `${item.bodyText.slice(0, 60)}...` : item.bodyText}</span>
          </li>
        ))}
      </ul>
      <Link to="/app/recorrentes" className="mt-2 inline-block text-xs text-primary hover:underline">
        Gerenciar publicações recorrentes
      </Link>
    </div>
  );
}
